import { Autocomplete, Box, createFilterOptions, InputAdornment, Paper, styled, TextField } from '@mui/material'
import { XIcon } from '@phosphor-icons/react'
import { CaretDown } from 'ComponentLibrary/src/atoms/Select/Select'
import { assembleUserSearchText, TaskAssignee, User } from 'ComponentLibrary/src/common/users'
import { DisplayUserLink } from 'ComponentLibrary/src/piazza/text'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { useState } from 'react'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

export type TaskSelectionOnChange = (selectedAssignee: TaskAssignee | null) => void

type TaskAssigneeSelectionProps = {
    assignee: TaskAssignee | null
    onChangeAssignee: TaskSelectionOnChange
    options: User[]
    userLinkCopy?: Partial<UserLinkCopy>
}

export function TaskAssigneeSelection({ assignee, options, onChangeAssignee, userLinkCopy }: TaskAssigneeSelectionProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = () => {
        setIsFocused(true)
        setIsOpen(true) // Ensure we open the popup whenever focus is received
    }

    const taskAssigneeUser = options.find((user) => user.id === assignee?.id) || null

    return (
        <Autocomplete
            blurOnSelect
            popupIcon={<CaretDown />}
            open={isOpen}
            onFocus={handleFocus}
            onOpen={() => {
                setIsOpen(true)
            }}
            clearIcon={<XIcon size={tokens.SizingIconS} weight="bold" />}
            onClose={() => setIsOpen(false)}
            value={assignee}
            onChange={(_, value) => {
                onChangeAssignee(value)
            }}
            PaperComponent={(props) => <StyledPaper {...props} />}
            options={options}
            groupBy={(option) => option.organization_name || ''}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-groupLabel': {
                            backgroundColor: tokens.ColorBackgroundDark,
                        },
                    },
                },
            }}
            renderOption={(props, option) => {
                return (
                    <Box component="li" {...props}>
                        <DisplayUserLink user={option} size="medium" showProfileOnHover={false} copy={userLinkCopy} />
                    </Box>
                )
            }}
            getOptionKey={(option) => option.id}
            getOptionLabel={() => ''} // Never render this, so the input value stays empty when selecting an option
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment:
                                isOpen || isFocused ? undefined : (
                                    <InputAdornment position="start">
                                        <DisplayUserLink user={taskAssigneeUser ?? undefined} size="medium" copy={userLinkCopy} />
                                    </InputAdornment> // Hack to render a react node in the place of the input value
                                ),
                        }}
                        sx={{
                            // Also hack to make rendering a react node as an input value possible
                            '& .MuiInputBase-input': {
                                cursor: 'inherit', // Inherit cursor from parent
                                opacity: isOpen ? 1 : 0,
                            },
                            '& .MuiInputBase-root': {
                                borderRadius: tokens.SizingCornerS,
                                backgroundColor: tokens.ColorBackgroundDark,
                                cursor: 'pointer',
                                '&.Mui-focused': {
                                    cursor: 'text',
                                },
                                padding: tokens.SizingSpacing150,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'unset',
                            },
                        }}
                    />
                )
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(items, state) =>
                filterOptions(
                    items.filter((user) => user.is_active !== false),
                    state
                )
            }
        />
    )
}

const filterOptions = createFilterOptions<TaskAssignee>({
    stringify: assembleUserSearchText,
})

const StyledPaper = styled(Paper)({
    borderRadius: tokens.SizingCornerM,
    backgroundColor: tokens.ColorBackgroundDark,
    ...tokens.Flyout,
})
